import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Task } from '../../../../types/task';
import { TaskItemV2 } from './TaskItemV2';
import { DraggableTimelineTask } from './DraggableTimelineTask';
import { getTaskTimeInfo, sortTasksByTime } from '../../../../utils/taskSortUtils';
import { calculateTimelineLayout } from '../../../../utils/timelineLayoutUtils';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { taskV2PixelsPerHourAtom } from '../../../../atoms/v3/taskV2Atoms';
import { usePinchZoom } from '../../../../hooks/v3/usePinchZoom';

interface TimelineViewProps {
  tasks: Task[];
  onTaskToggle: (taskId: string, completed: boolean) => void;
  onTaskClick: (taskId: string) => void;
  onTaskUpdate?: (updateData: any) => Promise<boolean>;
  onNewTask?: (initialData: { startAt: string; cost: number }) => void; // 新增：创建任务回调
  currentDate?: Date; // 新增：当前日期，用于创建任务时设置正确的日期
  stackMode?: boolean; // 新增：堆叠模式
}

interface TimelineTask extends Task {
  startTime: Date;
  duration: number; // 分钟
  timelineHeight: number; // 像素
}

/**
 * 时间轴视图组件
 * 按时间比例显示任务
 */
export const TimelineView: React.FC<TimelineViewProps> = ({
  tasks,
  onTaskToggle,
  onTaskClick,
  onTaskUpdate,
  onNewTask,
  currentDate,
  stackMode = false
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'v3.tasks' });
  const [isDark] = useAtom(isDarkAtom);
  const pixelsPerHour = useAtomValue(taskV2PixelsPerHourAtom);

  // 拖拽状态
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ y: number; hour: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ y: number; hour: number } | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // 触摸状态跟踪
  const touchStartRef = useRef<{ x: number; y: number; timestamp: number } | null>(null);

  // Pinch 缩放功能
  const { bindEvents, isGesturing } = usePinchZoom({
    minScale: 0.5,
    maxScale: 3.0,
    sensitivity: 0.8,
    enabled: true
  });

  // 绑定 pinch 事件到时间轴容器
  const timelineContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (timelineContainerRef.current) {
      const cleanup = bindEvents(timelineContainerRef.current);
      return cleanup;
    }
  }, [bindEvents]);

  // 处理时间轴数据
  const timelineData = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      return { timelineTasks: [], startHour: 8, endHour: 22, totalHeight: 0 };
    }

    // 过滤有时间的任务并排序
    const tasksWithTimeInfo = tasks.filter(task => {
      const timeInfo = getTaskTimeInfo(task);
      return timeInfo.hasTime && timeInfo.primaryTime;
    });

    // 应用排序
    const sortedTasks = sortTasksByTime(tasksWithTimeInfo);

    // 转换为时间轴任务格式
    const timelineTasks = sortedTasks.map(task => {
      const timeInfo = getTaskTimeInfo(task);
      const startTime = timeInfo.primaryTime!;
      const duration = task.cost ? Math.max(task.cost / 60, 15) : 30; // 最少15分钟，默认30分钟

      return {
        ...task,
        id: task.id,
        startHour: startTime.getHours() + startTime.getMinutes() / 60,
        endHour: (startTime.getHours() + startTime.getMinutes() / 60) + (duration / 60),
        duration,
        timelineHeight: Math.max((duration / 60) * pixelsPerHour, 44), // 最小44像素
      };
    });

    if (timelineTasks.length === 0) {
      return { timelineTasks: [], startHour: 8, endHour: 22, totalHeight: 0 };
    }

    // 计算时间范围
    const startTimes = timelineTasks.map(t => t.startHour);
    const endTimes = timelineTasks.map(t => t.endHour);

    const startHour = Math.max(0, Math.floor(Math.min(...startTimes)) - 1);
    const endHour = Math.min(24, Math.ceil(Math.max(...endTimes)) + 1);

    // 使用新的布局算法
    const layoutResult = calculateTimelineLayout(timelineTasks, stackMode, pixelsPerHour);

    return {
      timelineTasks: layoutResult.tasks,
      startHour,
      endHour,
      totalHeight: layoutResult.totalHeight
    };

  }, [tasks, stackMode, pixelsPerHour]);

  // 生成时间刻度
  const timeMarks = useMemo(() => {
    const marks = [];
    for (let hour = timelineData.startHour; hour <= timelineData.endHour; hour++) {
      marks.push({
        hour,
        label: `${hour.toString().padStart(2, '0')}:00`
      });
    }
    return marks;
  }, [timelineData.startHour, timelineData.endHour]);

  // 计算当前时间在时间轴上的位置
  const currentTimePosition = useMemo(() => {
    const now = new Date();
    const currentHour = now.getHours() + now.getMinutes() / 60;

    if (stackMode) {
      // 堆叠模式：找到当前时间最接近的任务位置
      const currentTasks = timelineData.timelineTasks.filter(task =>
        task.startHour <= currentHour && task.endHour > currentHour
      );

      if (currentTasks.length > 0) {
        // 如果有正在进行的任务，指向第一个任务的位置
        return currentTasks[0].stackPosition || 0;
      } else {
        // 如果没有正在进行的任务，找最接近的任务
        const futureTasks = timelineData.timelineTasks.filter(task => task.startHour > currentHour);
        if (futureTasks.length > 0) {
          return futureTasks[0].stackPosition || 0;
        }
        // 如果没有未来任务，指向最后一个任务后面
        const lastTask = timelineData.timelineTasks[timelineData.timelineTasks.length - 1];
        return (lastTask?.stackPosition || 0) + (lastTask?.stackHeight || 44);
      }
    } else {
      // 等比例模式：按时间比例计算
      if (currentHour < timelineData.startHour || currentHour > timelineData.endHour) {
        return null;
      }
      const offsetFromStart = currentHour - timelineData.startHour;
      return offsetFromStart * pixelsPerHour;
    }
  }, [timelineData.startHour, timelineData.endHour, timelineData.timelineTasks, stackMode, pixelsPerHour]);

  // 获取当前时间的显示文本
  const currentTimeText = useMemo(() => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  }, []);

  // 将像素位置转换为小时
  const pixelToHour = useCallback((y: number) => {
    const hourOffset = y / pixelsPerHour;
    return timelineData.startHour + hourOffset;
  }, [timelineData.startHour, pixelsPerHour]);

  // 将小时转换为像素位置
  const hourToPixel = useCallback((hour: number) => {
    return (hour - timelineData.startHour) * pixelsPerHour;
  }, [timelineData.startHour, pixelsPerHour]);

  // 处理拖拽开始
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!onNewTask) return;

    // 检查是否点击在任务项上，如果是则不开始拖拽
    const target = e.target as HTMLElement;
    if (target.closest('[data-task-item]')) {
      return;
    }

    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    const y = e.clientY - rect.top;
    const hour = pixelToHour(y);

    setIsDragging(true);
    setDragStart({ y, hour });
    setDragEnd({ y, hour });

    e.preventDefault();
  }, [onNewTask, pixelToHour]);

  // 处理拖拽移动
  const handleDragMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !dragStart) return;

    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    const y = e.clientY - rect.top;
    const hour = pixelToHour(y);

    setDragEnd({ y, hour });
  }, [isDragging, dragStart, pixelToHour]);

  // 处理拖拽结束
  const handleDragEnd = useCallback(() => {
    if (!isDragging || !dragStart || !dragEnd || !onNewTask) {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    // 计算开始和结束时间
    const startHour = Math.min(dragStart.hour, dragEnd.hour);
    const endHour = Math.max(dragStart.hour, dragEnd.hour);

    // 最小持续时间15分钟
    const minDuration = 0.25; // 15分钟 = 0.25小时
    const duration = Math.max(endHour - startHour, minDuration);

    // 创建开始时间 - 使用当前日期而不是当前时间
    const startTime = currentDate ? new Date(currentDate) : new Date();
    startTime.setHours(Math.floor(startHour), Math.round((startHour % 1) * 60), 0, 0);

    // 计算持续时间（秒）
    const costInSeconds = Math.round(duration * 3600);

    // 调用创建任务回调
    onNewTask({
      startAt: startTime.toISOString(),
      cost: costInSeconds
    });

    // 重置拖拽状态
    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  }, [isDragging, dragStart, dragEnd, onNewTask]);

  // 处理触摸开始
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!onNewTask) return;

    // 检查是否在 Pinch 缩放手势中，如果是则不处理
    if (isGesturing) return;

    // 检查是否点击在任务项上，如果是则不开始拖拽
    const target = e.target as HTMLElement;
    if (target.closest('[data-task-item]')) {
      return;
    }

    const touch = e.touches[0];
    if (!touch) return;

    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    // 记录触摸开始位置，但不立即进入拖拽模式
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY - rect.top,
      timestamp: Date.now()
    };
  }, [onNewTask, isGesturing]);

  // 处理触摸移动
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current || isDragging) return;

    const touch = e.touches[0];
    if (!touch) return;

    const rect = timelineRef.current?.getBoundingClientRect();
    if (!rect) return;

    const currentY = touch.clientY - rect.top;
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(currentY - touchStartRef.current.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // 移动距离超过5px时进入拖拽模式
    if (distance > 5) {
      e.preventDefault(); // 阻止滚动等默认行为

      // 计算开始位置的小时
      const startHour = pixelToHour(touchStartRef.current.y);
      const currentHour = pixelToHour(currentY);

      // 进入拖拽模式
      setIsDragging(true);
      setDragStart({ y: touchStartRef.current.y, hour: startHour });
      setDragEnd({ y: currentY, hour: currentHour });

      // 清理触摸跟踪状态
      touchStartRef.current = null;
    }
  }, [isDragging, pixelToHour]);

  // 处理触摸结束
  const handleTouchEnd = useCallback(() => {
    // 清理触摸跟踪状态
    touchStartRef.current = null;

    // 如果在拖拽状态，调用拖拽结束处理
    if (isDragging) {
      handleDragEnd();
    }
  }, [isDragging, handleDragEnd]);

  if (timelineData.timelineTasks.length === 0) {
    return (
      <div className={`flex items-center justify-center py-12 min-h-[400px] ${isDark ? 'bg-ios-dark-gray6' : 'bg-white'}`}>
        <div className={`text-center ${isDark ? 'text-ios-dark-gray2' : 'text-gray-500'}`}>
          <svg className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-ios-dark-gray3' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm">{t('emptyState.noTimelineTasks')}</p>
        </div>
      </div>
    );
  }

  // 计算总高度
  const totalTimelineHeight = (timelineData.endHour - timelineData.startHour) * pixelsPerHour + timelineData.totalHeight;

  return (
    <div className={isDark ? 'bg-ios-dark-gray6' : 'bg-white'}>
      {/* 时间轴容器 */}
      <div
        ref={timelineContainerRef}
        className="relative"
        style={{
          // 在 pinch 手势期间禁用其他交互
          pointerEvents: isGesturing ? 'none' : 'auto'
        }}
      >
        {/* 时间刻度 */}
        <div
          className={`absolute left-0 top-0 w-16 border-r ${
            isDark
              ? 'bg-ios-dark-gray5 border-ios-dark-gray4'
              : 'bg-gray-50 border-gray-200'
          }`}
          style={{ height: `${Math.max(totalTimelineHeight, 400)}px` }}
        >
          {stackMode ? (
            // 堆叠模式：时间刻度根据任务分布动态生成
            timelineData.timelineTasks.map((task, index) => (
              <div
                key={task.id}
                className={`absolute flex items-start pt-2 px-2 border-b ${
                  isDark ? 'border-ios-dark-gray4' : 'border-gray-100'
                }`}
                style={{
                  top: `${task.stackPosition || 0}px`,
                  height: `${task.stackHeight || 44}px`,
                  width: '100%'
                }}
              >
                <span className={`text-xs font-mono ${
                  isDark ? 'text-ios-dark-gray2' : 'text-gray-500'
                }`}>
                  {`${Math.floor(task.startHour).toString().padStart(2, '0')}:${Math.round((task.startHour % 1) * 60).toString().padStart(2, '0')}`}
                </span>
              </div>
            ))
          ) : (
            // 等比例模式：传统的小时刻度
            timeMarks.map(mark => (
              <div
                key={mark.hour}
                className={`flex items-start pt-2 px-2 border-b ${
                  isDark ? 'border-ios-dark-gray4' : 'border-gray-100'
                }`}
                style={{ height: `${pixelsPerHour}px` }}
              >
                <span className={`text-xs font-mono ${
                  isDark ? 'text-ios-dark-gray2' : 'text-gray-500'
                }`}>
                  {mark.label}
                </span>
              </div>
            ))
          )}
        </div>

        {/* 任务列表 */}
        <div
          ref={timelineRef}
          className="ml-16 relative"
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ cursor: isDragging ? 'grabbing' : (onNewTask ? 'crosshair' : 'default') }}
        >
          {/* 当前时间指示线 */}
          {currentTimePosition !== null && (
            <div
              className="absolute left-0 right-0 z-10 pointer-events-none"
              style={{ top: `${currentTimePosition}px` }}
            >
              {/* 时间标签 */}
              <div className="absolute left-0 transform -translate-x-full -translate-y-1/2 pr-2">
                <span className={`text-xs font-medium px-1 rounded shadow-sm ${
                  isDark
                    ? 'text-blue-400 bg-ios-dark-gray6'
                    : 'text-blue-600 bg-white'
                }`}>
                  {currentTimeText}
                </span>
              </div>
              {/* 指示线 */}
              <div className="w-full h-0.5 bg-blue-500 shadow-sm"></div>
              {/* 右侧圆点 */}
              <div className="absolute right-0 top-0 w-2 h-2 bg-blue-500 rounded-full transform translate-x-1/2 -translate-y-1/2"></div>
            </div>
          )}

          {/* 拖拽预览 */}
          {isDragging && dragStart && dragEnd && (
            <div
              className={`absolute left-0 right-0 z-20 pointer-events-none border-2 border-dashed rounded-lg ${
                isDark
                  ? 'border-blue-400 bg-blue-900/20'
                  : 'border-blue-500 bg-blue-50/50'
              }`}
              style={{
                top: `${Math.min(hourToPixel(dragStart.hour), hourToPixel(dragEnd.hour))}px`,
                height: `${Math.abs(hourToPixel(dragEnd.hour) - hourToPixel(dragStart.hour))}px`,
                minHeight: '44px' // 最小高度
              }}
            >
              <div className={`absolute left-2 top-2 text-xs font-medium ${
                isDark ? 'text-blue-400' : 'text-blue-600'
              }`}>
                {t('emptyState.newTask')}
              </div>
            </div>
          )}

          {timelineData.timelineTasks.map((task, index) => {
            let topPosition: number;
            let taskHeight: number;
            let taskWidth: string;
            let taskLeft: string;

            if (stackMode) {
              // 堆叠模式：Google Calendar 风格的垂直排列
              topPosition = task.stackPosition || 0;
              taskHeight = task.stackHeight || 44;
              taskWidth = '100%'; // 全宽
              taskLeft = '0%'; // 左对齐

              console.log(`堆叠任务 ${task.id}:`, {
                stackPosition: task.stackPosition,
                stackHeight: task.stackHeight,
                topPosition,
                taskHeight
              });
            } else {
              // 等比例模式：按时间比例和重叠布局
              const offsetFromStart = task.startHour - timelineData.startHour;
              topPosition = offsetFromStart * pixelsPerHour;
              taskHeight = task.timelineHeight;
              taskWidth = `${(task.layoutWidth || 1) * 100}%`;
              taskLeft = `${(task.layoutLeft || 0) * 100}%`;

              console.log(`等比例任务 ${task.id}:`, {
                startHour: task.startHour,
                offsetFromStart,
                topPosition,
                taskHeight,
                layoutWidth: task.layoutWidth,
                layoutLeft: task.layoutLeft
              });
            }

            return (
              <div
                key={task.id}
                className="absolute"
                data-task-item="true"
                style={{
                  top: `${topPosition}px`,
                  left: taskLeft,
                  width: taskWidth,
                  height: `${taskHeight}px`,
                  zIndex: timelineData.timelineTasks.length - index,
                  paddingRight: stackMode ? '0' : ((task.layoutWidth || 1) < 1 ? '2px' : '0') // 堆叠模式不需要间距
                }}
              >
                {onTaskUpdate ? (
                  <DraggableTimelineTask
                    task={task}
                    onToggle={onTaskToggle}
                    onClick={onTaskClick}
                    onTaskUpdate={onTaskUpdate}
                    timelineHeight={taskHeight}
                    startHour={timelineData.startHour}
                    endHour={timelineData.endHour}
                    pixelsPerHour={pixelsPerHour}
                  />
                ) : (
                  <TaskItemV2
                    task={task}
                    onToggle={onTaskToggle}
                    onClick={onTaskClick}
                    viewMode="timeline"
                    timelineHeight={taskHeight}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* 设置容器高度 */}
        <div
          style={{
            height: `${Math.max(totalTimelineHeight, 400)}px`
          }}
        />
      </div>
    </div>
  );
};
